import type { SpotConditionsView, SpotView } from "@auto-pools/core";
import {
  formatMoney,
  formatSignedAmount,
  formatSignedPercent,
  formatToken,
  signTone,
} from "../../lib/format";
import {
  spotAllocation,
  spotCoinsValue,
  spotExitCell,
  spotPercentOf,
  spotTradingCost,
} from "./spotPresentation";

const toneClass = (value: string | null) => {
  const tone = signTone(value);
  return tone === "positive" ? "positive" : tone === "negative" ? "negative" : "";
};

/**
 * A signed USDT result (Exit now PnL / vs BTC HODL): the percentage of its own base on top, the
 * signed USDT amount under it. Both come from the server's USDT difference; the percentage exists
 * only when its base is known and positive.
 */
function SignedResult({ amount, base }: { amount: string | null; base: string | null }) {
  const percent = spotPercentOf(amount, base);
  return (
    <>
      <b className={percent === null ? "unknown" : toneClass(amount)}>
        {percent === null ? "—" : formatSignedPercent(percent)}
      </b>
      <span className={`num spot-metric-sub ${toneClass(amount)}`}>
        {formatSignedAmount(amount, "USDT")}
      </span>
    </>
  );
}

/**
 * A plain null-guard chain (no `&&`/`||` on the member accesses) so the type predicate narrows
 * `btcClose`/`sma100` to `string` at every call site without fighting the optional-chain lint rule.
 */
function hasBtcAverage(
  conditions: SpotConditionsView | null,
): conditions is SpotConditionsView & { btcClose: string; sma100: string } {
  if (conditions === null) return false;
  if (conditions.btcClose === null) return false;
  if (conditions.sma100 === null) return false;
  return true;
}

/**
 * The seven columns (design 50/51/52): geometry never changes between "in coins", "waiting" (all
 * in USDT) and a needs-attention overlay — only what each column is worth saying, per the design's
 * own mapping table. `—` plus the reason stands in wherever the server has not provided a number.
 */
export function SpotMetricsStrip({ spot }: { spot: SpotView }) {
  const { card } = spot;
  const waiting = spot.running === "waiting";
  const inCoins = spot.coins.length > 0;
  const coinsValue = spotCoinsValue(spot);
  const cost = spotTradingCost(card);
  const exit = spotExitCell(spot);
  const allocation = spotAllocation(spot);
  const conditions = spot.conditions;
  const altCount = conditions
    ? [
        conditions.btcOn,
        conditions.breadth !== null && conditions.breadth >= conditions.breadthMin,
        conditions.med30 !== null && conditions.med30 >= conditions.med30Min,
      ].filter(Boolean).length
    : null;

  return (
    <div className="compact-metrics spot-metrics">
      <section className="spot-metric-cell">
        <small>{waiting ? "Result since start" : "Exit now PnL"}</small>
        <SignedResult amount={card.exitNowPnl} base={card.contributed} />
        <span className="num spot-metric-sub">
          {card.exitNowPnl === null
            ? "unavailable · no fresh market read"
            : waiting && spot.state === "running"
              ? "nothing is at risk right now"
              : spot.reason === "sale-failing"
                ? "if the sale goes through at current prices"
                : "if everything is sold into USDT now, after fees"}
        </span>
      </section>
      <section className="spot-metric-cell">
        <small>vs BTC HODL</small>
        <SignedResult amount={card.vsBtcHodl} base={card.btcHodl} />
        {card.contributed !== null && (
          <span className="num spot-metric-sub">
            vs putting {formatToken(card.contributed)} USDT into BTC
          </span>
        )}
      </section>
      <section className="spot-metric-cell">
        <small>BTC vs 100-day average</small>
        {!hasBtcAverage(conditions) ? (
          <>
            <b className="unknown">—</b>
            <span className="spot-metric-sub">decision data unavailable</span>
          </>
        ) : (
          <>
            <b>
              {(() => {
                const delta = (Number(conditions.btcClose) / Number(conditions.sma100) - 1) * 100;
                return Number.isFinite(delta)
                  ? `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`
                  : "—";
              })()}
            </b>
            <span className="num spot-metric-sub">
              {conditions.btcOn ? "above it" : "below it"}
            </span>
            {conditions.alt ? (
              <span className="num spot-metric-sub positive">
                alt season on · {String(altCount)} of 3
              </span>
            ) : (
              altCount !== null && (
                <span className="num spot-metric-sub">
                  alt season off · {String(altCount)} of 3
                </span>
              )
            )}
          </>
        )}
      </section>
      <section className="spot-metric-cell">
        <small>Trading cost</small>
        <b
          className={
            cost.total === null ? "unknown" : signTone(cost.total) === "positive" ? "negative" : ""
          }
        >
          {cost.total === null ? "—" : formatMoney(cost.total)}
        </b>
        <span className="num spot-metric-sub">
          {cost.fees === null ? "fees unavailable" : `fees ${formatMoney(cost.fees)}`} ·{" "}
          {cost.slippage === null
            ? "slippage unavailable"
            : `slippage ${formatMoney(cost.slippage)}`}
        </span>
        <span className="spot-metric-sub">paid on fills so far</span>
      </section>
      {inCoins ? (
        <section className="spot-metric-cell">
          <small>Value in coins</small>
          <b className={coinsValue === null ? "unknown" : ""}>
            {coinsValue === null ? "—" : formatMoney(coinsValue)}
          </b>
          <span className="num spot-metric-sub">
            {card.freeUsdt === null
              ? "free USDT unavailable"
              : `+${formatMoney(card.freeUsdt)} free USDT`}
          </span>
        </section>
      ) : (
        <section className="spot-metric-cell">
          <small>In USDT</small>
          <b className={card.value === null ? "unknown" : ""}>
            {card.value === null ? "—" : formatMoney(card.value)}
          </b>
          <span className="num spot-metric-sub">
            {card.value !== null
              ? "whole subaccount"
              : spot.readingSubaccount
                ? "reading the subaccount"
                : "not read yet"}
          </span>
        </section>
      )}
      <section className="spot-metric-cell">
        <small>Allocation</small>
        {allocation === null ? (
          <span className="num spot-metric-sub unknown">— · allocation not available yet</span>
        ) : (
          <>
            <div className="spot-allocation-bar" aria-hidden="true">
              <div
                className="spot-allocation-calm"
                style={{ width: `${String(allocation.calmPercent)}%` }}
              />
              <div
                className="spot-allocation-season"
                style={{ width: `${String(allocation.seasonPercent)}%` }}
              />
              <div
                className="spot-allocation-usdt"
                style={{ width: `${String(allocation.usdtPercent)}%` }}
              />
            </div>
            <span className="num spot-metric-sub">
              {waiting
                ? "USDT 100 %"
                : `calm ${allocation.calmPercent.toFixed(0)} % · season ${allocation.seasonPercent.toFixed(0)} % · USDT ${allocation.usdtPercent.toFixed(0)} %`}
            </span>
          </>
        )}
      </section>
      <section className="spot-metric-cell">
        <small>{exit.label}</small>
        <b className={exit.tone}>{exit.value}</b>
        <span className="num spot-metric-sub">{exit.note}</span>
      </section>
    </div>
  );
}
